import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Trash2, Pencil, Building2, RefreshCw, Copy, Eye, EyeOff } from "lucide-react";

function genPassword() {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%";
  return Array.from({ length: 14 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

export default function DsiTeam() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", hotel_ids: [] });
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  const myHotelIds = user?.hotel_ids || [];

  const load = () => api.get("/dsi/users").then((r) => setStaff(r.data));
  const loadHotels = () =>
    api.get("/hotels").then((r) =>
      setHotels(r.data.filter((h) => myHotelIds.includes(h.id)))
    );

  useEffect(() => { load(); loadHotels(); }, []);

  const openCreate = () => {
    setEditUser(null);
    setForm({ email: "", name: "", password: "", hotel_ids: [] });
    setShowPwd(false);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ email: u.email, name: u.name, password: "", hotel_ids: u.hotel_ids || [] });
    setShowPwd(false);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editUser) {
        await api.put(`/dsi/users/${editUser.id}`, {
          name: form.name,
          hotel_ids: form.hotel_ids,
          password: form.password || undefined,
        });
        toast.success("Sous-utilisateur mis à jour");
      } else {
        await api.post("/dsi/users", form);
        toast.success("Sous-utilisateur créé");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async () => {
    try {
      await api.delete(`/dsi/users/${confirmDel.id}`);
      toast.success(`${confirmDel.name} supprimé`);
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleHotel = (id) =>
    setForm((f) => ({
      ...f,
      hotel_ids: f.hotel_ids.includes(id)
        ? f.hotel_ids.filter((h) => h !== id)
        : [...f.hotel_ids, id],
    }));

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">DSI</p>
          <h1 className="mt-1 font-head text-3xl font-black tracking-tighter text-zinc-900">Mon équipe</h1>
        </div>
        <Button onClick={openCreate} className="rounded-sm bg-blue-600 hover:bg-blue-700">
          <Plus className="mr-1 h-4 w-4" /> Nouveau membre
        </Button>
      </div>

      <div className="mt-6 rounded-sm border border-zinc-200 bg-white">
        {staff.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-400">
            Aucun sous-utilisateur. Ajoutez un membre de votre équipe.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-zinc-500">
                <th className="px-4 py-3 pr-4">Nom</th>
                <th className="px-4 py-3 pr-4">Email</th>
                <th className="px-4 py-3 pr-4">Hôtels</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {staff.map((u) => (
                <tr key={u.id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-medium text-zinc-800">{u.name}</td>
                  <td className="px-4 py-3 font-mono text-[13px] text-zinc-500">{u.email}</td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {(u.hotel_ids || []).length} hôtel(s)
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(u)}
                        className="rounded-sm p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => setConfirmDel(u)}
                        className="rounded-sm p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Dialog créer / éditer */}
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight">
              {editUser ? "Modifier le membre" : "Nouveau membre"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Nom</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 rounded-sm text-sm" placeholder="Marie Martin" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                disabled={!!editUser}
                className="mt-1 rounded-sm text-sm disabled:bg-zinc-50 disabled:text-zinc-400"
                placeholder="marie@hotel.fr"
              />
            </div>
            <div>
              <Label className="text-xs">
                {editUser ? "Nouveau mot de passe (laisser vide pour ne pas changer)" : "Mot de passe"}
              </Label>
              <div className="mt-1 flex items-center gap-1.5">
                <div className="relative flex-1">
                  <Input
                    type={showPwd ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    className="rounded-sm pr-8 font-mono text-sm"
                    placeholder={editUser ? "••••••••" : "Requis"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                  >
                    {showPwd ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </div>
                <button
                  type="button"
                  title="Générer un mot de passe"
                  onClick={() => { const p = genPassword(); setForm((f) => ({ ...f, password: p })); setShowPwd(true); }}
                  className="rounded-sm border border-zinc-200 px-2 py-2 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800 transition-colors"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
                {form.password && (
                  <button
                    type="button"
                    title="Copier le mot de passe"
                    onClick={() => { navigator.clipboard.writeText(form.password); toast.success("Mot de passe copié"); }}
                    className="rounded-sm border border-zinc-200 px-2 py-2 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800 transition-colors"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Hôtels</p>
              <div className="flex flex-wrap gap-2">
                {hotels.map((h) => (
                  <button key={h.id} type="button" onClick={() => toggleHotel(h.id)}
                    className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-xs font-medium transition-colors ${
                      form.hotel_ids.includes(h.id)
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-zinc-200 text-zinc-500 hover:border-zinc-400"
                    }`}>
                    <Building2 className="h-3 w-3" /> {h.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-sm bg-blue-600 hover:bg-blue-700" onClick={save} disabled={saving}>
              {editUser ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog suppression */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight">Supprimer le membre</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            Supprimer <span className="font-semibold">{confirmDel?.name}</span> ?
            Ce membre n'aura plus accès aux hôtels.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setConfirmDel(null)}>Annuler</Button>
            <Button className="rounded-sm bg-red-600 hover:bg-red-700" onClick={deleteUser}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
